import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="adventures-map"
export default class extends Controller {
  static targets = ["map", "centerMarker", "nearbyMarkers", "latitude", "longitude", "location"]

  static values = {
    markers: Array,
  }

  map = null;
  centerLatLng = null;
  markers = [];
  infoWindows = [];
  editMode = false;

  connect() {
    this.centerLatLng = this.targetToData(this.centerMarkerTarget);
    this.editMode = this.hasLatitudeTarget;
    this.placementMode = this.editMode && !this.centerMarkerTarget.dataset.latitude; // New adventure without coordinates
    this.initMap().then(() => {
      this.showCurrentMarker();
      this.showNearbyMarkers();
      this.correctZoomLevel();
      this.setupMapClick();
    });
  }

  async initMap() {
    const { Map } = await google.maps.importLibrary("maps");

    this.map = new Map(this.mapTarget, {
      center: this.centerLatLng,
      zoom: 13,
      mapId: "map"
    })
  }

  async showCurrentMarker() {
    if (this.centerMarkerTarget.dataset.latitude && this.centerMarkerTarget.dataset.longitude) {
      this.createMarker(this.centerMarkerTarget, true);
    }
  }

  correctZoomLevel() {
    const centerLatLng = this.targetToData(this.centerMarkerTarget);
    
    if (this.nearbyMarkersTargets.length > 0) {
      const furthestMarkerDistance = this.nearbyMarkersTargets.reduce((max, marker) => {
        const distanceInKm = google.maps.geometry.spherical.computeDistanceBetween(centerLatLng, this.targetToData(marker)) / 1000;
        return Math.max(max, distanceInKm);
      }, 0);
      
      const coordDelta = furthestMarkerDistance * 0.008;
      const swBound = new google.maps.LatLng(centerLatLng.lat - coordDelta, centerLatLng.lng - coordDelta);
      const neBound = new google.maps.LatLng(centerLatLng.lat + coordDelta, centerLatLng.lng + coordDelta);
      const bounds = new google.maps.LatLngBounds(swBound, neBound);
      const mapDim = { height: this.mapTarget.clientHeight, width: this.mapTarget.clientWidth };
      console.log(mapDim)
      const zoomLevel = this.calculateZoomLevel(bounds, mapDim);
      this.map.setZoom(zoomLevel);
    }
  }

  calculateZoomLevel(bounds, mapDim) {
    var WORLD_DIM = { height: 256, width: 256 };
    var ZOOM_MAX = 21;

    function latRad(lat) {
        var sin = Math.sin(lat * Math.PI / 180);
        var radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx, worldPx, fraction) {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    var ne = bounds.getNorthEast();
    var sw = bounds.getSouthWest();

    var latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    var lngDiff = ne.lng() - sw.lng();
    var lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

    var latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    var lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }

  showNearbyMarkers() {
    this.nearbyMarkersTargets.forEach(marker => {
      this.createMarker(marker);
    })
  }

  targetToData(target) {
    const lat = parseFloat(target.dataset.latitude);
    const lng = parseFloat(target.dataset.longitude);
    
    if (isNaN(lat) || isNaN(lng)) {
      return { lat: 52.2493382, lng: 4.4933081 }; // Default to Netherlands
    }
    
    return {
      lat: lat + (Math.random() * 0.0001) - 0.00005,
      lng: lng + (Math.random() * 0.0001) - 0.00005
    }
  }

  async createMarker(target, currentMarker = false) {
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    const latLng = this.targetToData(target);
    const icon = document.querySelector(`.marker-icon-${target.dataset.category}`).cloneNode(true);
    const iconEl = icon.querySelector('img');
    iconEl.classList = `ag-bg-${target.dataset.category} ag-map-marker-icon`;
    if (currentMarker) {
      iconEl.classList.add("ag-map-marker-icon-current");
    }
    
    const marker = new AdvancedMarkerElement({
      position: latLng,
      map: this.map,
      content: iconEl,
      gmpDraggable: currentMarker && this.editMode
    })
    this.markers.push(marker);
    
    let stars = "";
    for (let i = 0; i < target.dataset.score; i++) {
      stars += `<i class="fa fa-star"></i>`
    }

    let content = `
      <div class="ag-map-marker-info-window">
        <img src="${target.dataset.image}" alt="${target.dataset.title}" />
        <div class="title">${target.dataset.title}</div>
        <div class="score">${stars}</div>
        <a href="/adventures/${target.dataset.id}/edit" target="_blank" class="ag-map-marker-info-window-link"><i class="fa fa-arrow-right"></i></a>
      </div>`

    const infoWindow = new google.maps.InfoWindow({
      content: content
    })
    this.infoWindows.push(infoWindow);
    marker.addListener("click", () => {
      this.infoWindows.forEach(window => {
        window.close();
      })
      infoWindow.open({
        anchor: marker,
        map: this.map
      })
    })

    if (currentMarker) {
      marker.addListener("dragend", this.markerMoved.bind(this, marker))
    }
  }

  markerMoved(marker) {
    const position = marker.position;
    this.latitudeTarget.value = position.lat;
    this.longitudeTarget.value = position.lng;
    this.getAddress(position.lat, position.lng);
  }

  getAddress(lat, lng) {
    const lang = window.navigator.language.split("-")[0];
    const url = `/api/devices/geocode?lat=${lat}&lng=${lng}&language=${lang}&include_country=true`
    fetch(url, {
      method: "GET",
    }).then(response => response.text()).then(data => {
      console.log(data)
      let city_country = data.replace(/\d{2,4}\s?\w{2}/, "||").split("||")[1];

      if (!city_country) {
        city_country = data;
      }
      
      if (city_country && city_country.includes("+")) {
        city_country = city_country.replace(/\+.{2}/, "").trim();
      }
      
      if (this.locationTarget) {
        this.locationTarget.innerHTML = city_country ? city_country.trim() : 'Location not found';
      }
    }).catch(error => {
      console.error('Error fetching address:', error);
      if (this.locationTarget) {
        this.locationTarget.innerHTML = 'Location not found';
      }
    })
  }

  showAdventure(event) {
    const target = event.target.closest('.small-adventure-card');
    const adventureId = target.dataset.adventureId;
    const adventureLatitude = parseFloat(target.dataset.latitude);
    const adventureLongitude = parseFloat(target.dataset.longitude);

    this.map.panTo({ lat: adventureLatitude, lng: adventureLongitude });

    this.infoWindows.forEach(window => {
      window.close();
    })
    this.infoWindows[target.dataset.index].open({
      anchor: this.markers[target.dataset.index],
      map: this.map
    })

    // center the selected target in its container horizontally
    const container = target.closest('.scrollable-adventures-container');
    container.scrollTo({
      left: target.offsetLeft - container.offsetLeft + (target.offsetWidth / 2) - (container.offsetWidth / 2),
      behavior: 'smooth'
    });

  }

  setupMapClick() {
    if (this.placementMode) {
      this.map.addListener("click", (event) => {
        this.placeMarker(event.latLng);
      });
    }
  }

  async placeMarker(latLng) {
    if (this.placementMarker) {
      this.placementMarker.map = null;
    }

    const instruction = document.querySelector('.map-instruction');
    if (instruction) {
      instruction.style.display = 'none';
    }

    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    const icon = document.querySelector('.marker-icon-other').cloneNode(true);
    const iconEl = icon.querySelector('img');
    iconEl.classList = 'ag-bg-other ag-map-marker-icon ag-map-marker-icon-current';

    this.placementMarker = new AdvancedMarkerElement({
      position: latLng,
      map: this.map,
      content: iconEl,
      gmpDraggable: true
    });

    this.latitudeTarget.value = latLng.lat();
    this.longitudeTarget.value = latLng.lng();

    this.getAddress(latLng.lat(), latLng.lng());

    this.placementMarker.addListener("dragend", () => {
      const position = this.placementMarker.position;
      this.latitudeTarget.value = position.lat;
      this.longitudeTarget.value = position.lng;
      this.getAddress(position.lat, position.lng);
    });
  }
}
